import BaseProduct from './BaseProduct';
import { ProductId, CpcHorizon, CpcVariable } from '../types';
import store, { getLayerOpacity } from '../state';
import maplibregl from 'maplibre-gl';

export class CpcOutlookLayer extends BaseProduct {
  public readonly id: ProductId = 'cpc_outlook';
  private pinnedPopup: maplibregl.Popup | null = null;
  private hoverPopup: maplibregl.Popup | null = null;
  private mapEventsBound: boolean = false;
  private unsubscribeStore: (() => void) | null = null;
  private currentHorizon: CpcHorizon = '6_10';
  private currentVariable: CpcVariable = 'temp';

  protected onActivate(): void {
    const state = store.getState();
    this.currentHorizon = (state.cpcHorizon as CpcHorizon) || '6_10';
    this.currentVariable = (state.cpcVariable as CpcVariable) || 'temp';

    this.refresh();
    this.bindMapEvents();

    this.unsubscribeStore = store.subscribe((state) => {
      if (!this.active) return;
      const newHorizon = (state.cpcHorizon as CpcHorizon) || '6_10';
      const newVar = (state.cpcVariable as CpcVariable) || 'temp';

      if (newHorizon !== this.currentHorizon || newVar !== this.currentVariable) {
        this.currentHorizon = newHorizon;
        this.currentVariable = newVar;
        this.refresh();
      }
    });
  }

  protected onDeactivate(): void {
    if (this.unsubscribeStore) {
      this.unsubscribeStore();
      this.unsubscribeStore = null;
    }
    this.removeLayers();
    this.unbindMapEvents();
  }

  protected onTimeUpdate(_date: string, _hour: string): void {
    this.refresh();
  }

  public refresh(): void {
    if (!this.active) return;
    this.setupLayers();
    this.updateVisibility();
  }

  private getDataUrl(): string {
    const state = store.getState();
    const manifest = state.manifest;
    let apiServer = manifest?.api_server;
    if (apiServer === undefined || apiServer === null) {
      apiServer = 'https://auth.skyskout.com/';
    } else if (apiServer === '') {
      const path = window.location.pathname;
      const dir = path.substring(0, path.lastIndexOf('/') + 1);
      apiServer = window.location.origin + dir;
    }
    if (!apiServer.endsWith('/')) apiServer += '/';
    const tileRoot = (manifest?.tile_base || 'tiles').replace(/^\/+|\/+$/g, '');
    const versionParam = manifest?.generated_at ? `?v=${encodeURIComponent(manifest.generated_at)}` : `?t=${Date.now()}`;

    const filename = `cpc_${this.currentHorizon}_${this.currentVariable}.geojson`;
    return `${apiServer}${tileRoot}/forecast/cpc_outlooks/${filename}${versionParam}`;
  }

  private setupLayers(): void {
    const dataUrl = this.getDataUrl();
    const sourceId = 'cpc-outlook-source';

    if (!this.map.getSource(sourceId)) {
      this.map.addSource(sourceId, {
        type: 'geojson',
        data: dataUrl
      });
    } else {
      const source = this.map.getSource(sourceId) as maplibregl.GeoJSONSource;
      if (source && typeof source.setData === 'function') {
        source.setData(dataUrl);
      }
    }

    const layers = this.map.getStyle()?.layers;
    let beforeId: string | undefined = undefined;
    if (layers) {
      const firstSymbol = layers.find(l => l.type === 'symbol');
      if (firstSymbol) beforeId = firstSymbol.id;
    }

    const opacity = getLayerOpacity(this.id, 1.0);

    // 1. Fill Layer
    if (!this.map.getLayer('cpc-outlook-fill')) {
      this.map.addLayer({
        id: 'cpc-outlook-fill',
        type: 'fill',
        source: sourceId,
        paint: {
          'fill-color': ['coalesce', ['get', 'fill_color'], '#38bdf8'],
          'fill-opacity': 0.75 * opacity
        }
      }, beforeId);
    } else {
      this.map.setPaintProperty('cpc-outlook-fill', 'fill-color', ['coalesce', ['get', 'fill_color'], '#38bdf8']);
      this.map.setPaintProperty('cpc-outlook-fill', 'fill-opacity', 0.75 * opacity);
    }

    // 2. Stroke Layer
    if (!this.map.getLayer('cpc-outlook-stroke')) {
      this.map.addLayer({
        id: 'cpc-outlook-stroke',
        type: 'line',
        source: sourceId,
        paint: {
          'line-color': ['coalesce', ['get', 'stroke_color'], '#0284c7'],
          'line-width': 1.5,
          'line-opacity': 0.95 * opacity
        }
      }, beforeId);
    } else {
      this.map.setPaintProperty('cpc-outlook-stroke', 'line-color', ['coalesce', ['get', 'stroke_color'], '#0284c7']);
      this.map.setPaintProperty('cpc-outlook-stroke', 'line-opacity', 0.95 * opacity);
    }
  }

  private updateVisibility(): void {
    const visibility = this.active ? 'visible' : 'none';
    if (this.map.getLayer('cpc-outlook-fill')) {
      this.map.setLayoutProperty('cpc-outlook-fill', 'visibility', visibility);
    }
    if (this.map.getLayer('cpc-outlook-stroke')) {
      this.map.setLayoutProperty('cpc-outlook-stroke', 'visibility', visibility);
    }
  }

  private removeLayers(): void {
    if (this.pinnedPopup) {
      this.pinnedPopup.remove();
      this.pinnedPopup = null;
    }
    if (this.hoverPopup) {
      this.hoverPopup.remove();
      this.hoverPopup = null;
    }
    if (this.map.getLayer('cpc-outlook-stroke')) {
      this.map.removeLayer('cpc-outlook-stroke');
    }
    if (this.map.getLayer('cpc-outlook-fill')) {
      this.map.removeLayer('cpc-outlook-fill');
    }
    if (this.map.getSource('cpc-outlook-source')) {
      this.map.removeSource('cpc-outlook-source');
    }
  }

  private bindMapEvents(): void {
    if (this.mapEventsBound) return;
    this.mapEventsBound = true;

    this.map.on('mouseenter', 'cpc-outlook-fill', () => {
      this.map.getCanvas().style.cursor = 'pointer';
    });

    this.map.on('mouseleave', 'cpc-outlook-fill', () => {
      this.map.getCanvas().style.cursor = '';
      if (this.hoverPopup && !this.pinnedPopup) {
        this.hoverPopup.remove();
        this.hoverPopup = null;
      }
    });

    this.map.on('mousemove', 'cpc-outlook-fill', (e) => {
      if (this.pinnedPopup) return;
      if (!e.features || e.features.length === 0) return;
      const feat = e.features[0];
      const props = feat.properties || {};

      const content = this.renderPopupContent(props);
      if (!this.hoverPopup) {
        this.hoverPopup = new maplibregl.Popup({
          closeButton: false,
          closeOnClick: false,
          className: 'cpc-tooltip-popup'
        });
      }
      this.hoverPopup.setLngLat(e.lngLat).setHTML(content).addTo(this.map);
    });

    this.map.on('click', 'cpc-outlook-fill', (e) => {
      if (!e.features || e.features.length === 0) return;
      const feat = e.features[0];
      const props = feat.properties || {};

      if (this.hoverPopup) {
        this.hoverPopup.remove();
        this.hoverPopup = null;
      }
      if (this.pinnedPopup) {
        this.pinnedPopup.remove();
      }

      const content = this.renderPopupContent(props, true);
      this.pinnedPopup = new maplibregl.Popup({
        closeButton: true,
        closeOnClick: true,
        className: 'cpc-pinned-popup'
      })
        .setLngLat(e.lngLat)
        .setHTML(content)
        .addTo(this.map);

      this.pinnedPopup.on('close', () => {
        this.pinnedPopup = null;
      });
    });
  }

  private unbindMapEvents(): void {
    if (!this.mapEventsBound) return;
    this.mapEventsBound = false;
    this.map.getCanvas().style.cursor = '';
  }

  private renderPopupContent(props: any, isPinned = false): string {
    const cat = props.cat || 'Outlook';
    const prob = props.prob || 33;
    const label = props.label || `${prob}% ${cat}`;
    const fillColor = props.fill_color || '#38bdf8';
    const period = props.valid_period || '';
    const fcstDate = props.fcst_date || '';
    const desc = props.description || '';
    const varType = props.var_type === 'precip' ? 'Precipitation' : 'Temperature';
    const horizonLabel = props.horizon === 'three_month' ? '3-Month' : (props.horizon === 'monthly' ? 'Monthly' : (props.horizon === '8_14' ? '8-14 Day' : '6-10 Day'));
    const isDarkBg = ['#285300', '#28600a', '#007814', '#009620', '#082566', '#133980', '#265999', '#990000', '#cc0000', '#663d00', '#8c5a0f', '#804000', '#4f2f2f'].includes(fillColor.toLowerCase());

    return `
      <div style="font-family: inherit; font-size: 13px; color: #f8fafc; line-height: 1.4; padding: 2px;">
        <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; gap: 8px;">
          <span style="font-weight: 700; font-size: 13px; color: #38bdf8;">
            📈 NOAA CPC ${horizonLabel} ${varType}
          </span>
          <span style="background: ${fillColor}; color: ${isDarkBg ? '#ffffff' : '#0f172a'}; font-weight: 700; font-size: 11px; padding: 2px 7px; border-radius: 4px; white-space: nowrap;">
            ${label}
          </span>
        </div>
        ${period ? `
        <div style="font-size: 11px; color: #cbd5e1; margin-bottom: 5px;">
          <strong>Target Period:</strong> ${period}
        </div>` : ''}
        ${desc ? `
        <div style="font-size: 12px; color: #e2e8f0; margin-bottom: 6px; line-height: 1.35;">
          ${desc}
        </div>` : ''}
        <div style="font-size: 10px; color: #94a3b8; border-top: 1px solid rgba(255,255,255,0.12); padding-top: 4px; display: flex; justify-content: space-between;">
          <span>Model: NOAA Climate Prediction Center</span>
          ${fcstDate ? `<span>Issued: ${fcstDate}</span>` : ''}
        </div>
        ${isPinned ? '<div style="font-size: 10px; color: #64748b; margin-top: 4px; text-align: right;">(Pinned • Click outside to close)</div>' : ''}
      </div>
    `;
  }
}

export default CpcOutlookLayer;
